/* eslint-disable max-len */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchTeamActivity, updateUserTimelogFromWebSocket, updateUserStatusFromWebSocket } from 'store/container/teamActivity/teamActivity-slice';
import { AugmentedUser } from 'types';
import { setRequestComponent } from 'utils/requestSource';
import { TIME_ENTRY_WS_EVENT, wsActionToStatus, TimeEntryWsEvent } from 'services/timeTracking';
import useWebSocket from 'utils/useWebSocket';

interface UseAvatarSheetReturn {
  users: AugmentedUser[];
  loading: boolean;
  isRevalidating: boolean;
  hasEverFetched: boolean;
  currentUserData: any | null;
  currentUserLoading: boolean;
  error: boolean;
  refetch: () => void;
}

export const useAvatarSheet = (open: boolean): UseAvatarSheetReturn => {
  const { users, loading, isRevalidating, hasEverFetched, currentUserData, currentUserLoading, error } = useSelector((store: RootState) => store.teamActivity);
  const dispatch = useDispatch();
  const debounceTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const refetch = useCallback(() => {
    dispatch(fetchTeamActivity());
  }, [dispatch]);

  // Listen for real-time time entry updates
  useWebSocket({
    channelName: 'model.changes',
    privateChannel: true,
    listen: {
      event: TIME_ENTRY_WS_EVENT,
      callback: (event: TimeEntryWsEvent) => {
        try {
          const { user_uuid: userId, source_title: taskTitle } = event.payload;
          const status = wsActionToStatus[event.action];

          if (userId) {
            // Clear pending debounce timeout if any
            if (debounceTimeoutsRef.current[userId]) {
              clearTimeout(debounceTimeoutsRef.current[userId]);
              delete debounceTimeoutsRef.current[userId];
            }

            const dispatchUpdate = () => dispatch(updateUserTimelogFromWebSocket({ userId, status, taskTitle }));

            if (status === 'paused') {
              // Debounce the paused state to absorb a pause-then-resume flicker
              debounceTimeoutsRef.current[userId] = setTimeout(() => {
                dispatchUpdate();
                delete debounceTimeoutsRef.current[userId];
              }, 500);
            } else {
              // running / terminal actions (completed, updated, deleted) apply immediately
              dispatchUpdate();
            }
          }
        } catch (error) {
          console.error('Error processing time entry event:', error);
        }
      },
    },
  });

  // Listen for real-time user status updates
  useWebSocket({
    channelName: 'model.changes',
    privateChannel: true,
    listen: {
      event: '.app.models.auth.user',
      callback: (event: any) => {
        try {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { activity_status } = event.payload || event;
          const { id } = event;

          if (id && activity_status !== undefined) {
            dispatch(updateUserStatusFromWebSocket({
              userId: id,
              activityStatus: activity_status,
            }));
          }
        } catch (error) {
          console.error('Error processing user status event:', error);
        }
      },
    },
  });

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeoutsRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      debounceTimeoutsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (open) {
      setRequestComponent('AvatarSheet');
      dispatch(fetchTeamActivity());
    } else {
      setRequestComponent('');
    }

    return () => {
      setRequestComponent('');
    };
  }, [open, dispatch]);

  return {
    users,
    loading,
    isRevalidating,
    hasEverFetched,
    currentUserData,
    currentUserLoading,
    error,
    refetch,
  };
};
